<template>
  <li>
    <div class="relative mt-[14px]">
      <!-- Verified icon outside NuxtLink -->
      <div v-if="isVerified" class="absolute z-10 top-[-14px] rtl:left-[-10px] rtl:right-auto left-auto right-[-10px] text-[0.813rem] font-bold rtl:rounded-[5px_0px] rounded-[0px_5px]">
        <img src="@/assets/icons/verify_white.svg" class="w-[30px] h-[30px]" alt :width="30" :height="30" />
      </div>

      <NuxtLink :to="productLink" aria-label="product" @click.prevent="isDisabled && $event.preventDefault()" :class="`${isDisabled ? 'pointer-events-none cursor-not-allowed' : ''}`">
        <div v-if="item"
            :class="`w-[200px] h-[200px] product__card__hover relative ease-linear p-1.5 rounded-[0.625rem] duration-[0.15s] transition-[box-shadow,border-color] border hover:border-secondary/25 active:border-secondary/30 ${isVerified ? 'border-[#0000FF] border-solid border-2' : 'border-[#E9E9E9]'} bg-white overflow-hidden`">
            <div class="relative h-full w-full cursor-pointer rounded-[0.438rem] overflow-hidden">
                <!-- product image -->
                <div :class="`${countdown == 0 ? `after:bg-[#0a0a0acf]` : 'after:bg-[#0a0a0a82]'} after:content-[''] after:absolute after:inset-0 z-0 inline-block product_image w-full h-full absolute inset-0`.trim()"
                    :title="item && item[finalizedNameKey] ? trimText(item[finalizedNameKey], 100) : ''">
                        <img @error="onImageError"
                            class="w-full h-full block object-cover object-center rounded-[0.438rem] xl:rounded-[0.46rem]"
                            :src="item.image || placeholderImg" alt="" loading="lazy" width="200" height="200"
                            draggable="false" />
                </div>
                <!-- offer has ended type -->
                <template v-if="countdown == 0">
                    <div class="absolute inset-0 font-bold text-[0.875rem] md:text-[0.925rem] text-[#FF1F00] grid place-content-center">
                        <strong>{{ $t('general.offer_ended') }}</strong>
                    </div>
                </template>
                <!-- other types -->
                <template v-else>
                    <!-- sale discount percentage - top -->
                    <div class="absolute rotate-[45deg] rtl:rotate-[-45deg] top-1 md:top-2 rtl:left-[-2rem] rtl:right-auto left-auto right-[-1.8rem]">
                        <strong class="block text-[1rem] py-[0.2rem] px-[2.5rem] ltr:pr-[1.8rem] rtl:px-[2.5rem] text-center bg-[#FF1F00] text-white">
                            {{ discountPercentage }}
                        </strong>
                    </div>
                    <!-- product price - bottom -->
                    <div class="absolute bottom-8 rtl:right-3 rtl:left-auto right-auto left-3 text-white">
                        <span class="flex items-center gap-1.5 w-max font-normal text-[0.8rem]">
                            <strong v-if="item.price" class="text-[1rem] md:text-[1.15rem] 3xl:text-[1.2rem]">{{ arabizeNumbers(Math.ceil(Number(getPrice)).toLocaleString()) }}</strong>
                            <span class="text-[0.85rem]">{{ $t('general.egy_pounds') }}</span>
                        </span>                           
                        <!-- discount -->
                        <span class="discount-line after:bg-white bg-[#FF1F00] text-white w-max pr-2 pl-3 rtl:pl-2 rtl:pr-3 py-1 font-normal text-[0.8rem] rounded-[0.25rem] whitespace-nowrap">{{ arabizeNumbers(Math.ceil(getPriceBasicDiscount)) }}</span>
                    </div>

                    <!--Product type - top -->
                    <span 
                        v-if="productType && item?.product_type != 'retail'"
                        class="gap-1 w-max min-w-[4rem] text-center px-2 py-1 absolute top-4 rtl:right-3 bg-primary text-white rtl:left-auto right-auto left-3 font-normal text-[0.8rem] flex items-center rounded-[0.25rem]">
                        <span class="mx-auto">{{ productType }}</span>
                    </span> 
                </template>

                <!-- offer remaning time label -->
                <div v-if="!isDisabled" class="absolute bottom-0 right-0 left-0 z-5">
                    <span :class="`${countdown == 0 ? 'bg-[#F9BF60]' : 'bg-primary' } w-full px-3 md:px-3.5 ellipsis-1 gap-2 py-1.5 text-white text-center font-bold text-[0.85rem] md:text-[0.9rem] flex items-center`.trim()">
                        <strong class="w-full mx-auto">{{ offerRemainingTimeCountDown }}</strong>
                    </span>
                </div>
            </div>
        </div>
      </NuxtLink>
    </div>
  </li>
</template>

<script>
import placeholderImg from "@/assets/images/placeholder(doNotRemove).jpg";
import { trimText, arabizeNumbers, roundWithHalf, onImageError, buildProductLink } from "@/helpers";

export default {
    setup() {
        const usedCookieLanguage = useCookie('localStorageCopy')?.value?.language || 'ar';

        return {
            usedCookieLanguage
        }
    },
    data() {
        return {
            placeholderImg,
            countdown: '',
            timer: null
        };
    },
    props: {
        item: {
            type: Object,
            default: () => ({}),
            required: true
        },
    },
    computed: {
        isVerified(){
            return this.item?.is_verified;
        },
        finalizedNameKey() {
            return this.nameKey || `name_${this.usedCookieLanguage}`;
        },
        productLink() {
            return buildProductLink(this.item);
        },
        productType(){
            switch(this.item?.product_type){
                case "wholesale":
                    return this.$t('general.wholesale');
                case "retail":
                    return this.$t('general.retail');
                case "offer":
                    return this.$t('general.offer_noun');
                default:
                    return "";
            }
        },       
        getPriceBasicDiscount(){
            switch(this.item?.product_type){
                case "wholesale":
                    return this.item?.wholesale_details[0]?.price;
                case "retail":
                    return this.item?.basic_price || this.item?.price;
                case "offer":
                    return this.item?.offer_details[0]?.new_price;
                default:
                    return 0;
            }
        },
        getPrice(){
            switch(this.item?.product_type){
                case "offer":
                    return parseFloat(this.item?.offer_details[0]?.new_price) * (1 - parseFloat(this.item?.discount) / 100).toFixed(2);
                case "retail":
                    return parseFloat(this.item?.price_after_discount);
                case "wholesale":
                    return parseFloat(this.item?.wholesale_details[0]?.price) * (1 - parseFloat(this.item?.discount) / 100).toFixed(2);
                default:
                    return 0;
            }
        },
        offerRemainingTimeCountDown() {
            return this.countdown;
        },
        offerRemaningTimeType(){
            return this.item?.label?.[this.anyKey('label')];
        },
        offerRemainingTimeId(){
            return this.item?.label?.id;
        },
        discountPercentage(){
            return arabizeNumbers(this.$t('general.percentage_label', {percentage: Math.round(this.item?.discount)}));
        },
        isDisabled(){
            return this.countdown == 0;
        }
    },
    methods: {
        arabizeNumbers,
        trimText,
        roundWithHalf,
        onImageError,
        updateCountdown() {
            if (!this.item?.end_date) {
                this.countdown = this.offerRemaningTimeType;
                return;
            }

            const now = Date.now();
            const remainingTime = new Date(this.item.end_date);
            const timeDiff = remainingTime - now;

            if (timeDiff <= 0) {
                clearInterval(this.timer);
                this.countdown = 0;
                return;
            }

            // Calculate all time units
            const months = Math.floor(timeDiff / (1000 * 60 * 60 * 24 * 30));
            const weeks = Math.floor((timeDiff % (1000 * 60 * 60 * 24 * 30)) / (1000 * 60 * 60 * 24 * 7));
            const days = Math.floor((timeDiff % (1000 * 60 * 60 * 24 * 7)) / (1000 * 60 * 60 * 24));
            const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);

            // Create array of non-zero time units
            const timeUnits = [
                { value: months, label: this.usedCookieLanguage === 'ar' ? 'ش' : 'M' },
                { value: weeks, label: this.usedCookieLanguage === 'ar' ? 'أ' : 'w' },
                { value: days, label: this.usedCookieLanguage === 'ar' ? 'ي' : 'd' },
                { value: hours, label: this.usedCookieLanguage === 'ar' ? 'س' : 'h' },
                { value: minutes, label: this.usedCookieLanguage === 'ar' ? 'د' : 'm' },
                { value: seconds, label: this.usedCookieLanguage === 'ar' ? 'ث' : 's' }
            ].filter(unit => unit.value > 0);

            // Take the first 3 significant time units
            const unitsToShow = timeUnits.slice(0, 3);

            // Format the countdown display
            let timeString = '';
            unitsToShow.reverse().forEach((unit, index) => {
                timeString += arabizeNumbers(unit.value) + unit.label + ' ';
            });

            this.countdown = timeString.trim();
        },
    },
    mounted() {
        this.updateCountdown();
        this.timer = setInterval(this.updateCountdown, 1000);
    },
    beforeUnmount() {
        if (this.timer) {
            clearInterval(this.timer);
        }
    }
};
</script>

<style lang="scss" scoped>

</style>